"use client";

import {
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type Ref,
  useCallback,
  useState,
} from "react";
import { Icon, IconButton, TextField, usePubSub } from "@huub-web/ui";
import { useTranslation } from "react-i18next";

import { useTemplates } from "../../hooks";

export interface QuickChatTemplateProps {
  placeholders?: string[];
}

/**
 * A template for quick chat functionality
 */
export const QuickChatTemplate: FC<
  QuickChatTemplateProps & { ref?: Ref<HTMLDivElement> }
> = ({ placeholders, ref, ...props }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const resolvedPlaceholder = placeholders?.[0] ?? t("quickChat.placeholder");
  const { lockPrompts, lockUI } = useTemplates();
  const pubSub = usePubSub();

  const sendMessage = useCallback(() => {
    if (!value) {
      return;
    }

    pubSub.publish("sendPrompt", { value });
    setValue("");
  }, [value, pubSub, setValue]);

  const handleButtonClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      sendMessage();
    },
    [sendMessage],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        sendMessage();
      }
    },
    [sendMessage],
  );

  return (
    <div ref={ref} {...props}>
      <TextField
        elevated
        placeholder={resolvedPlaceholder}
        leading={<Icon icon="aiFilled" color="gradient" />}
        value={value}
        onChange={setValue}
        onKeyDown={handleKeyDown}
        disabled={lockPrompts || lockUI}
        trailing={
          <div
            style={{
              opacity: `${!value || lockPrompts || lockUI ? 0 : 1}`,
            }}
          >
            <IconButton
              variant="opaque"
              disabled={!value || lockPrompts || lockUI}
              size="xs"
              icon="arrowRight"
              onClick={handleButtonClick}
              rounded
            />
          </div>
        }
      />
    </div>
  );
};
